import { Component, OnInit } from '@angular/core';
import {
  CandidateDataLoadState,
  CandidateListResultDetailsDto,
  CandidateMandateDistributionDto,
  CandidateResultDto,
  CountStatusDto,
  DetailClient,
  ElectoralCoalitionDto,
  ExportDataClient,
  ListCoalitionResultDto,
  ListMandateDistributionDto,
  OverviewClient,
  ResultSummaryDto,
  ResultType,
  VotingDetailsDto,
} from '@shared/clients/api.generated';
import { MapAreasMapping } from '@shared/components/map/map-styles.model';
import { SidenavigationLink } from '@shared/components/sidenavigation/sidenavigation.model';
import { SvgIcon } from '@valg-shared/utils/svg-icon';
import { TopCandidates } from '@shared/components/top-candidates/top-candidates.component';
import { CountStatus } from '@shared/components/count-status/count-status.component';
import { PartyOrCandidateListBase, PartyOrCandidateListDetail } from '@shared/components/expandable-party-list/expandable-party-table.model';
import { MandatesList } from '@shared/components/cards/mandates-list/mandates-list.model';
import { MandateCandidateListItem } from '@shared/components/cards/candidate-mandates/candidate-mandates.model';
import { VotingDetails, VotingDetailsList } from '@shared/components/voting-details/voting-details.model';
import { SidenavigationLinkType } from '@shared/components/page-layout/page-layout.model';
import { ActivatedRoute, Router } from "@angular/router";
import { saveFile } from "@valg-shared/utils/file-utils";
import { SpinnerService } from "@valg-shared/services/spinner/spinner.service";
import { ElectionOverviewService } from "../../election-overview/election-overview.service";
import { MapAreaClickedEvent } from "@shared/components/map/map-events.model";
import { TopCandidatesService } from "@shared/services/top-candidates.service";
import { ExportFileService } from "@valg-shared/services/export-file/export-file.service";
import { ExtendedElectionDto } from "../../election-overview/election-overview.model";

@Component({
  selector: 'valg-dk-details-region',
  templateUrl: './region.component.html',
  providers: [ SpinnerService ]
})
export class DetailsRegionComponent implements OnInit {
  election?: ExtendedElectionDto;
  region = "";

  regionId?: string;
  regionCode?: number;

  resultType: ResultType = ResultType.NoResults;
  candidates: PartyOrCandidateListBase[] = [];
  votingDetailsList: VotingDetailsList[] = [];
  votingDetails?: VotingDetails;

  electoralCoalitions?: ElectoralCoalitionDto[] = [];
  coalitionList?: ListCoalitionResultDto[] = [];
  mandatesList: MandatesList[] = [];
  candidateMandates: MandateCandidateListItem[] = [];
  mandateDistributionBasedOnMissingResults = false;

  readonly SvgIcon = SvgIcon;
  public mapAreasMapping?: MapAreasMapping;

  links: SidenavigationLink[] = [];
  filterLinks: SidenavigationLink[] = [];

  sideNavTitleType = SidenavigationLinkType.Municipality;
  parentLinkType = SidenavigationLinkType.Municipality;

  countStatusUpdated?: Date;
  countStatus: CountStatus[] = [];
  topCandidates: TopCandidates[] = [];
  candidateDataLoadState: CandidateDataLoadState = CandidateDataLoadState.NotLoaded;
  resultSummary!: ResultSummaryDto;

  constructor(
    private readonly detailClient: DetailClient,
    private readonly overviewClient: OverviewClient,
    private readonly electionService: ElectionOverviewService,
    private readonly exportDataClient: ExportDataClient,
    private readonly spinner: SpinnerService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly topCandidatesService: TopCandidatesService,
    private readonly exportFileService: ExportFileService,
    private readonly electionOverviewService: ElectionOverviewService
    ) {
    this.regionId = route.snapshot.params['regionId'];
  }

  async ngOnInit(): Promise<void> {
    await this.initializeCurrentElection();
    this.initializeNavigationList();
    this.initializeRegionDetails();
  }

  private async initializeCurrentElection() {
    this.election = await this.electionService.getCurrentElection();
  }

  private initializeCountStatusTable(result: CountStatusDto) {
    this.region = result.name;
    this.countStatusUpdated = result.lastUpdate;

    this.countStatus = [
      {
        name: "count_status.voting_percentage",
        percentage: result.percentage?.votingPercentage
      },
      {
        name: "count_status.voting_prec_count_percentage",
        percentage: result.percentage?.preCountPercentage
      },
      {
        name: "count_status.voting_fine_count_percentage",
        percentage: result.percentage?.fineCountPercentage
      }
    ];
  }

  private initializeElectoralCoalitionsTable(result?: ElectoralCoalitionDto[]) {
    this.electoralCoalitions = result;
  }

  private initializeNavigationList() {
    this.overviewClient.getRegionNavigation(this.regionId!).subscribe((result) => {
      this.links = this.electionOverviewService.mapRegion(result, this.regionId!);
    });

    this.overviewClient.getElectionSideNavigation().subscribe((result) => {
      this.filterLinks = this.electionOverviewService.mapRegionalSideNavigation(result);
    });
  }

  private initializeRegionDetails() {
    this.detailClient.getRegionDetails(this.regionId!)
      .subscribe(
        ({
           regionCode,
           mapAreaList,
           countStatusDto,
           resultType,
           electoralCoalitionList,
           listMandateDistributionDtos,
           candidateListResultDetailsList,
           candidateMandateDistributionList,
           listCoalitionResults,
           votingDetails,
           candidateResultDtos,
           candidateDataLoadState,
           resultSummaryDto,
           mandateDistributionBasedOnMissingResults
         }) => {
          this.resultSummary = resultSummaryDto;
          this.candidateDataLoadState = candidateDataLoadState;
          this.resultType = resultType;
          this.regionCode = regionCode;
          this.mapAreasMapping = this.electionService.toMapAreaMapping(mapAreaList);
          this.initializeCountStatusTable(countStatusDto);
          this.initializeCandidateListResultTable(candidateListResultDetailsList)
          this.initializeListMandateDistributionTable(listMandateDistributionDtos);
          this.initializeElectoralCoalitionsTable(electoralCoalitionList);
          this.initializeCandidateMandateDistributionListTable(candidateMandateDistributionList);
          this.initializeListCoalitionResultTable(listCoalitionResults);
          this.initializeVotingDetailsTable(votingDetails);
          this.initializeTopCandidatesTable(candidateResultDtos);
          this.mandateDistributionBasedOnMissingResults = mandateDistributionBasedOnMissingResults;
        });
  }

  private initializeTopCandidatesTable(result: CandidateResultDto[]) {
    this.topCandidates = this.topCandidatesService.fromCandidateResults(result);
  }

  private initializeListMandateDistributionTable(result: ListMandateDistributionDto[]) {
    this.mandatesList = result.map((listMandate) => {
      return {
        id: listMandate.id,
        candidateList: listMandate.displayName,
        numberOfMandates: listMandate.numberOfSeats
      }
    })
  }

  private initializeCandidateMandateDistributionListTable(result: CandidateMandateDistributionDto[]) {
    this.candidateMandates = result.map((candidate) => {
      return {
        id: candidate.id,
        candidate: candidate.displayName,
        numberOfMandates: candidate.mandateNumber
      }
    })
  }

  private initializeListCoalitionResultTable(listCoalitionResults?: ListCoalitionResultDto[]) {
    this.coalitionList = listCoalitionResults;
  }

  private initializeVotingDetailsTable(result: VotingDetailsDto) {
    this.votingDetails = {
        numberOfVoters: result.numberOfEligibleVoters,
        countedPollingAreas: {
          number: result.pollingAreasCount?.numberOfPollingAreasHavePreliminaryCount,
          total: result.pollingAreasCount?.totalPollingAreas
        },
        validVotes: {
          value: result.validVotesCount.validVotesTotal,
          diff: result.validVotesCount.changeComparedToPreviousElection
        },
        invalidVotes: {
          value: result.invalidVotesCount.invalidVotesTotal,
          diff: result.invalidVotesCount.changeComparedToPreviousElection
        },
        blankVotes: {
          value: result.blankVotesCount.blankVotesTotal,
          diff: result.blankVotesCount.changeComparedToPreviousElection
        },
        totalVotes: {
          value: result.totalVotesCount.totalVotes,
          diff: result.totalVotesCount.totalChangeComparedToPreviousElection
        }
      } as VotingDetails
  }

  private initializeCandidateListResultTable(result: CandidateListResultDetailsDto[]) {
    this.candidates = result.map((details) => {
      return {
        title: details.displayName,
        votes: this.resultType == ResultType.NoResults ? "" : details.numberOfVotes,
        diff: details.votingDifferencePreviousElection,
        percentage: details.percentages,
        percentage_diff: details.differencePercentages,
        listOrganisation: details.listOrganisationType,
        id: crypto.randomUUID(),
        children: details.candidateResultListWithListVotes.map((cr) => {
          return {
            name: cr.displayName,
            votes: cr.numberOfVotes,
            id: crypto.randomUUID()
          } as PartyOrCandidateListDetail
        })
      } as PartyOrCandidateListBase
    })
  }
  onDownloadReportClick() {
    this.spinner.attachSpinner(this.exportDataClient.exportRvDataCsv(this.election?.id, this.regionId, null,
      null)).subscribe(result => {
      const fileName = this.exportFileService.getReportFileNameWithDate(result.fileName, ".csv");
      saveFile(result.data, fileName);
    })
  }

  async onAreaClick($event: MapAreaClickedEvent) {
    const id = $event.data?.id;
    if (id) {
      const link = `municipality/${id}`
      await this.router.navigate([ link ], {
          relativeTo: this.route,
        }
      );
    }
  }
}
